import type {
  EventType,
  VendorCategory,
  ExpenseStatus,
  PaymentMethod,
  CatalogItem,
  CatalogItemUsage,
  CreateCatalogItemInput,
  UpdateCatalogItemInput,
  FloorPlanObjectTypeConfig,
  CreateFloorPlanObjectTypeInput,
  UpdateFloorPlanObjectTypeInput,
  EventTypeDefaultTag,
  CreateEventTypeDefaultTagInput,
  UpdateEventTypeDefaultTagInput,
} from '@peles/shared';
import { apiClient } from './client';

// ─── Event Types ─────────────────────────────────────────────────────────────

export async function fetchEventTypes(): Promise<EventType[]> {
  const { data } = await apiClient.get<EventType[]>('/settings/event-types');
  return data;
}

export async function createEventType(input: {
  name: string;
  nameHe?: string;
  description?: string;
}): Promise<EventType> {
  const { data } = await apiClient.post<EventType>('/settings/event-types', input);
  return data;
}

export async function updateEventType(
  id: string,
  updates: {
    name?: string;
    nameHe?: string | null;
    description?: string | null;
    isActive?: boolean;
    sortOrder?: number;
  },
): Promise<EventType> {
  const { data } = await apiClient.patch<EventType>(`/settings/event-types/${id}`, updates);
  return data;
}

// ─── Vendor Categories ───────────────────────────────────────────────────────

export async function fetchVendorCategories(): Promise<VendorCategory[]> {
  const { data } = await apiClient.get<VendorCategory[]>('/settings/vendor-categories');
  return data;
}

export async function createVendorCategory(input: {
  name: string;
  nameHe?: string;
}): Promise<VendorCategory> {
  const { data } = await apiClient.post<VendorCategory>('/settings/vendor-categories', input);
  return data;
}

export async function updateVendorCategory(
  id: string,
  updates: {
    name?: string;
    nameHe?: string | null;
    isActive?: boolean;
    sortOrder?: number;
  },
): Promise<VendorCategory> {
  const { data } = await apiClient.patch<VendorCategory>(
    `/settings/vendor-categories/${id}`,
    updates,
  );
  return data;
}

export async function deleteVendorCategory(id: string): Promise<void> {
  await apiClient.delete(`/settings/vendor-categories/${id}`);
}

// ─── Expense Statuses ────────────────────────────────────────────────────────

export async function fetchExpenseStatuses(): Promise<ExpenseStatus[]> {
  const { data } = await apiClient.get<ExpenseStatus[]>('/settings/expense-statuses');
  return data;
}

export async function createExpenseStatus(input: {
  name: string;
  nameHe?: string;
  color?: string;
  sortOrder?: number;
  isPaidInFull?: boolean;
}): Promise<ExpenseStatus> {
  const { data } = await apiClient.post<ExpenseStatus>('/settings/expense-statuses', input);
  return data;
}

export async function updateExpenseStatus(
  id: string,
  updates: {
    name?: string;
    nameHe?: string | null;
    color?: string | null;
    sortOrder?: number;
    isActive?: boolean;
    isPaidInFull?: boolean;
  },
): Promise<ExpenseStatus> {
  const { data } = await apiClient.patch<ExpenseStatus>(
    `/settings/expense-statuses/${id}`,
    updates,
  );
  return data;
}

// ─── Payment Methods ─────────────────────────────────────────────────────────

export async function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  const { data } = await apiClient.get<PaymentMethod[]>('/settings/payment-methods');
  return data;
}

export async function createPaymentMethod(input: {
  name: string;
  nameHe?: string;
  isProducerFunded?: boolean;
}): Promise<PaymentMethod> {
  const { data } = await apiClient.post<PaymentMethod>('/settings/payment-methods', input);
  return data;
}

export async function updatePaymentMethod(
  id: string,
  updates: {
    name?: string;
    nameHe?: string | null;
    isActive?: boolean;
    isProducerFunded?: boolean;
  },
): Promise<PaymentMethod> {
  const { data } = await apiClient.patch<PaymentMethod>(`/settings/payment-methods/${id}`, updates);
  return data;
}

// ─── Catalog Items ───────────────────────────────────────────────────────────

export async function fetchCatalogItems(): Promise<CatalogItem[]> {
  const { data } = await apiClient.get<CatalogItem[]>('/settings/catalog-items');
  return data;
}

export async function createSettingsCatalogItem(
  input: CreateCatalogItemInput,
): Promise<CatalogItem> {
  const { data } = await apiClient.post<CatalogItem>('/settings/catalog-items', input);
  return data;
}

export async function updateSettingsCatalogItem(
  id: string,
  updates: UpdateCatalogItemInput,
): Promise<CatalogItem> {
  const { data } = await apiClient.patch<CatalogItem>(`/settings/catalog-items/${id}`, updates);
  return data;
}

export async function fetchCatalogItemUsage(id: string): Promise<CatalogItemUsage> {
  const { data } = await apiClient.get<CatalogItemUsage>(`/settings/catalog-items/${id}/usage`);
  return data;
}

export async function deleteSettingsCatalogItem(
  id: string,
  reassignToCatalogItemId: string | null,
): Promise<void> {
  await apiClient.post(`/settings/catalog-items/${id}/delete`, { reassignToCatalogItemId });
}

// ─── Floor Plan Object Types ────────────────────────────────────────────────

export async function fetchFloorPlanObjectTypes(): Promise<FloorPlanObjectTypeConfig[]> {
  const { data } = await apiClient.get<FloorPlanObjectTypeConfig[]>(
    '/settings/floor-plan-object-types',
  );
  return data;
}

export async function createFloorPlanObjectType(
  input: CreateFloorPlanObjectTypeInput,
): Promise<FloorPlanObjectTypeConfig> {
  const { data } = await apiClient.post<FloorPlanObjectTypeConfig>(
    '/settings/floor-plan-object-types',
    input,
  );
  return data;
}

export async function updateFloorPlanObjectType(
  id: string,
  updates: UpdateFloorPlanObjectTypeInput,
): Promise<FloorPlanObjectTypeConfig> {
  const { data } = await apiClient.patch<FloorPlanObjectTypeConfig>(
    `/settings/floor-plan-object-types/${id}`,
    updates,
  );
  return data;
}

export async function deleteFloorPlanObjectType(id: string): Promise<void> {
  await apiClient.delete(`/settings/floor-plan-object-types/${id}`);
}

// ─── Event Type Default Tags ─────────────────────────────────────────────────

export async function listEventTypeDefaultTags(
  eventTypeId: string,
): Promise<EventTypeDefaultTag[]> {
  const { data } = await apiClient.get<EventTypeDefaultTag[]>(
    `/settings/event-types/${eventTypeId}/default-tags`,
  );
  return data;
}

export async function createEventTypeDefaultTag(
  eventTypeId: string,
  input: CreateEventTypeDefaultTagInput,
): Promise<EventTypeDefaultTag> {
  const { data } = await apiClient.post<EventTypeDefaultTag>(
    `/settings/event-types/${eventTypeId}/default-tags`,
    input,
  );
  return data;
}

export async function updateEventTypeDefaultTag(
  eventTypeId: string,
  tagId: string,
  updates: UpdateEventTypeDefaultTagInput,
): Promise<EventTypeDefaultTag> {
  const { data } = await apiClient.patch<EventTypeDefaultTag>(
    `/settings/event-types/${eventTypeId}/default-tags/${tagId}`,
    updates,
  );
  return data;
}

export async function deleteEventTypeDefaultTag(eventTypeId: string, tagId: string): Promise<void> {
  await apiClient.delete(`/settings/event-types/${eventTypeId}/default-tags/${tagId}`);
}
